// De velden bovenaan een notitie: status, project, deadline.
//
// Zoals in Obsidian: een klein blokje boven de tekst, geen apart scherm. Je typt een naam en
// een waarde, en dat is het. Namen die je eerder gebruikte staan in het keuzelijstje, want twee
// notities met "status" en "Status" zijn twee kolommen op je bord en dat wil je niet.
//
// Wat je hier invult komt in de front matter van de notitie te staan bij de export. Daarmee is
// het geen veld van deze app maar van jouw bestand — precies het verschil tussen "je kunt het
// meenemen" en "het staat in hun database".

import { useEffect, useRef, useState } from "react";
import { api, type EigenschapRegel } from "../api.ts";
import { Icoon } from "./Icoon.tsx";

type Regel = { sleutel: string; waarde: string };

export function Eigenschappen({
  notitieId,
  eigenschappen,
  bekend,
  opGewijzigd,
  opFout
}: {
  notitieId: string;
  eigenschappen: Record<string, string>;
  /** Wat je elders al gebruikte; voor de keuzelijstjes. */
  bekend: EigenschapRegel[];
  opGewijzigd: (velden: Record<string, string>) => void;
  opFout?: (tekst: string) => void;
}) {
  const [regels, setRegels] = useState<Regel[]>([]);
  const [open, setOpen] = useState(false);
  const idRef = useRef(notitieId);

  // Van buiten naar binnen: bij een andere notitie (of nadat de server hem teruggaf) opnieuw
  // beginnen. Niet tijdens het typen, want dan springt je cursor weg.
  useEffect(() => {
    const paren = Object.entries(eigenschappen).map(([sleutel, waarde]) => ({ sleutel, waarde }));
    setRegels(paren);
    if (idRef.current !== notitieId) {
      idRef.current = notitieId;
      setOpen(false);
    }
    if (paren.length > 0) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notitieId, JSON.stringify(eigenschappen)]);

  async function bewaar(nieuwe: Regel[]) {
    setRegels(nieuwe);
    const velden: Record<string, string> = {};
    for (const regel of nieuwe) {
      const sleutel = regel.sleutel.trim().toLowerCase();
      if (sleutel) velden[sleutel] = regel.waarde.trim();
    }
    try {
      const notitie = await api.zetEigenschappen(notitieId, velden);
      opGewijzigd(notitie.eigenschappen);
    } catch (fout) {
      opFout?.(`De eigenschappen zijn niet bewaard: ${(fout as Error).message}`);
    }
  }

  const waardenVan = (sleutel: string) =>
    bekend.find((b) => b.sleutel === sleutel.trim().toLowerCase())?.waarden ?? [];

  if (!open) {
    return (
      <button className="eigenschapknop" onClick={() => setOpen(true)}>
        <Icoon naam="plus" grootte={13} /> Eigenschap
      </button>
    );
  }

  return (
    <div className="eigenschappen">
      {regels.map((regel, index) => (
        <div className="eigenschaprij" key={index}>
          <input
            className="eigenschapsleutel"
            list="bekende-eigenschappen"
            value={regel.sleutel}
            placeholder="naam"
            onChange={(e) => setRegels(regels.map((r, i) => (i === index ? { ...r, sleutel: e.target.value } : r)))}
            onBlur={() => void bewaar(regels)}
          />
          <input
            className="eigenschapwaarde"
            list={`waarden-${index}`}
            value={regel.waarde}
            placeholder="waarde"
            onChange={(e) => setRegels(regels.map((r, i) => (i === index ? { ...r, waarde: e.target.value } : r)))}
            onBlur={() => void bewaar(regels)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          <datalist id={`waarden-${index}`}>
            {waardenVan(regel.sleutel).map((waarde) => (
              <option key={waarde} value={waarde} />
            ))}
          </datalist>
          <button
            className="knop stil"
            aria-label="Deze eigenschap weghalen"
            title="Weghalen"
            onClick={() => void bewaar(regels.filter((_, i) => i !== index))}
          >
            <Icoon naam="kruis" grootte={13} />
          </button>
        </div>
      ))}

      <datalist id="bekende-eigenschappen">
        {bekend.map((b) => (
          <option key={b.sleutel} value={b.sleutel} />
        ))}
      </datalist>

      <button className="eigenschapknop" onClick={() => setRegels([...regels, { sleutel: "", waarde: "" }])}>
        <Icoon naam="plus" grootte={13} /> Eigenschap
      </button>
    </div>
  );
}
